/**
 * Move given item in an array by given number of positions
 * @param array - Array to be modified
 * @param item - Item in given array
 * @param pos - Number of positions to move an item, positive moves forward and negative moves back
 */
export function moveItem<T>(array: T[], item: T, pos: number): void
{
    const index = array.indexOf(item);

    if (index >= 0)
    {
        const newPos = index + pos;

        if (newPos >= 0 && newPos < array.length)
        {
            const item = array.splice(index, 1)[0];

            array.splice(newPos, 0, item);
        }
    }
}

export function deepClone<T>(value: T, maxDepth = 64): T
{
    if (!maxDepth) throw new Error('Deep clone max depth reached');

    if (ArrayBuffer.isView(value))
    {
        return new (value as any).constructor(value);
    }
    else if (Array.isArray(value))
    {
        const array = [] as any;

        for (const i in value) array[i] = deepClone(value[i], maxDepth - 1);

        return array;
    }
    else if (typeof value === 'object')
    {
        const obj = {} as any;

        for (const f in value) obj[f] = deepClone(value[f], maxDepth - 1);

        return obj;
    }

    return value;
}

export function deepAssign<T>(target: T, ...sources: Partial<T>[]): T
{
    for (const source of sources)
    {
        for (const f in source)
        {
            (target as any)[f] = deepClone(source[f]);
        }
    }

    return target;
}

export function propsToText(obj: Record<string, string|number|boolean>): string
{
    const items = [];

    for (const f in obj)
    {
        items.push(`${f}:${String(obj[f])}`);
    }

    return items.join('\n');
}

export function textToProps(text: string):Record<string, string|number|boolean>
{
    const obj = {} as Record<string, string|number|boolean>;
    const items = text.split(/\r\n|\r|\n/);

    for (const item of items)
    {
        const parts = item.split(':');

        if (!parts[0]) continue;

        const field = String(parts[0]).trim();
        const value = parts[1] ? stringToPrimitiveValue(String(parts[1]).trim()) : true;

        if (value === null || value === undefined) continue;

        obj[field] = value;
    }

    return obj;
}

export function stringToPrimitiveValue(str: string): string|number|boolean|null
{
    if (str.toLowerCase() === 'true' || str.toLowerCase() === 'false') return str.toLowerCase() === 'true';

    return str.match(/^[-.0-9]+$/) ? parseFloat(str) : str;
}

